import type { FilterSchema } from '@/shared/components/filter/validation'
import type { BIResponse } from '@/shared/generated'
import type { WidgetPeriods } from '../types'

import React from 'react'
import { any, last, prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import {
	selectDefaultMetricsKeys,
	useBIAnalyticQuery,
	useMetricsQuery,
	zipSeries,
} from '@/entities/bi'
import { useSelectedCurrency } from '@/entities/currency'

import { Skeleton } from '@/shared/components/ui/skeleton'
import { formatMoney, NumberFormats } from '@/shared/tools/locale'

import PercentDifferenceBadge from '../../PercentDifferenceBadge'
import { Widget, WidgetContent, WidgetHeader } from '../../Widget'

export default function NumericRevenueWidget({
	commonPeriods,
	commonFilters,
}: {
	commonPeriods: WidgetPeriods
	commonFilters: FilterSchema[]
}) {
	const { t } = useTranslation()

	const { data: metrics, ...metricsQuery } = useMetricsQuery<string[]>({
		placeholderData: [],
		select: selectDefaultMetricsKeys,
	})
	const currency = useSelectedCurrency()
	const biCommonQuery = {
		currency,
		label: 'NumericRevenue',
		metrics: metrics!,
		dimensions: [],
		filters: [...commonFilters],
		date_aggregation: null,
	}
	const biQueryConfig = {
		enabled: !!metrics?.length,
		select: (data: BIResponse | null) =>
			prop('revenue', last(zipSeries(data?.meta, data?.data))) as number,
	}
	const biComparedPeriodQuery = useBIAnalyticQuery<number>(
		{
			...commonPeriods.compared,
			...biCommonQuery,
		},
		biQueryConfig
	)
	const biMainPeriodQuery = useBIAnalyticQuery<number>(
		{
			...commonPeriods.main,
			...biCommonQuery,
		},
		biQueryConfig
	)
	const isLoading = any(prop('isFetching'), [
		biMainPeriodQuery,
		biComparedPeriodQuery,
		metricsQuery,
	])
	const diff =
		(biMainPeriodQuery.data! * 100) / biComparedPeriodQuery.data! - 100

	return (
		<Widget className='relative flex flex-auto flex-col'>
			<WidgetHeader
				title={t('metric.revenue')}
				subtitle={t('app.based_on_last_integration')}
			/>
			<WidgetContent className='truncate'>
				{isLoading ? (
					<Skeleton className='col-span-2 h-10 w-32' />
				) : (
					<div className='flex flex-col space-y-1'>
						<div className='flex items-center space-x-2'>
							<PercentDifferenceBadge value={diff} />
							<span className='truncate text-xs text-muted-foreground'>
								({t('past')}:{' '}
								{formatMoney(
									biComparedPeriodQuery.data!,
									currency,
									NumberFormats.Compact
								)}
								)
							</span>
						</div>
						<span className='truncate text-3xl font-semibold'>
							{formatMoney(
								biMainPeriodQuery.data!,
								currency,
								NumberFormats.Compact
							)}
						</span>
					</div>
				)}
			</WidgetContent>
		</Widget>
	)
}
